import "./styles.css";
import { compassPoint, formatDistance, formatElevation, type HorizonResult, type VisiblePeak, type WaterResult } from "@summit/core";
import { PanoramaRenderer } from "./render/panorama";
import { getPosition, startCamera, stopCamera, watchOrientation, type Orientation } from "./sensors";
import type { WorkerRequest, WorkerResponse } from "./worker";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const canvas = $<HTMLCanvasElement>("view");
const video = $<HTMLVideoElement>("camera");
const renderer = new PanoramaRenderer(canvas);
const worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });

interface State {
  lat: number;
  lon: number;
  name: string;
  altitude: number | null;
  horizon: HorizonResult | null;
  peaks: VisiblePeak[];
  peaksFinal: boolean;
  water: WaterResult | null;
  showWater: boolean;
  compass: boolean;
  camera: boolean;
  /** Manual correction added to the sensor heading (drag while in compass mode). */
  headingOffset: number;
}
const state: State = {
  lat: NaN, lon: NaN, name: "", altitude: null, horizon: null, peaks: [], peaksFinal: false,
  water: null, showWater: localStorage.getItem("summit.water") !== "0", compass: false, camera: false, headingOffset: 0,
};
let requestId = 0;
let stopOrientation: (() => void) | null = null;

// ---------- URL state ----------
function readHash(): Partial<{ lat: number; lon: number; hdg: number; fov: number; pitch: number; name: string }> {
  const p = new URLSearchParams(location.hash.slice(1));
  const num = (k: string) => (p.has(k) ? parseFloat(p.get(k)!) : undefined);
  return { lat: num("lat"), lon: num("lon"), hdg: num("hdg"), fov: num("fov"), pitch: num("pitch"), name: p.get("name") ?? undefined };
}
let hashTimer = 0;
function writeHash(): void {
  clearTimeout(hashTimer);
  hashTimer = window.setTimeout(() => {
    if (!Number.isFinite(state.lat)) return;
    const p = new URLSearchParams();
    p.set("lat", state.lat.toFixed(5));
    p.set("lon", state.lon.toFixed(5));
    p.set("hdg", renderer.view.heading.toFixed(1));
    p.set("fov", renderer.view.fov.toFixed(0));
    if (Math.abs(renderer.view.pitch) > 0.5) p.set("pitch", renderer.view.pitch.toFixed(1));
    if (state.name) p.set("name", state.name);
    history.replaceState(null, "", "#" + p.toString());
  }, 250);
}

// ---------- UI helpers ----------
const toastEl = $("toast");
let toastTimer = 0;
function toast(msg: string, ms = 3500): void {
  toastEl.textContent = msg;
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toastEl.hidden = true), ms);
}
function setStatus(msg: string): void {
  $("status").textContent = msg;
}
const loading = $("loading");
function showLoading(stage: string, fraction: number, detail = ""): void {
  loading.hidden = false;
  $("loading-stage").textContent = stage;
  $("loading-bar").style.width = `${Math.round(fraction * 100)}%`;
  $("loading-detail").textContent = detail;
}
function hideLoading(): void {
  loading.hidden = true;
}
/**
 * Once the panorama is on screen the status line is the only feedback there is, so it carries both
 * what has been found and what is still being waited for, with a running clock. Peak names come
 * from OpenStreetMap's public servers, which can take a minute or more; a silent minute is
 * indistinguishable from a hang.
 */
let waitLabel = "";
let waitDetail = "";
let waitSince = 0;
let waitTimer = 0;
let waitWarned = false;

function beginWait(label: string, detail?: string): void {
  if (label !== waitLabel) {
    waitLabel = label;
    waitDetail = "";
    waitSince = Date.now();
  }
  if (detail) waitDetail = detail;
  if (!waitTimer) waitTimer = window.setInterval(updateStatus, 1000);
  updateStatus();
}

function endWait(): void {
  waitLabel = "";
  waitDetail = "";
  clearInterval(waitTimer);
  waitTimer = 0;
  updateStatus();
}

function updateStatus(): void {
  const bits: string[] = [];
  if (state.peaks.length) {
    bits.push(`${state.peaks.filter((p) => p.visible).length} peaks ${state.peaksFinal ? "visible" : "nearby"}`);
  }
  if (state.water && state.showWater) {
    const seen = new Set(state.water.spanFeature);
    if (seen.size) bits.push(`${seen.size} water ${seen.size === 1 ? "body" : "bodies"}`);
  }
  if (waitLabel) {
    const secs = Math.round((Date.now() - waitSince) / 1000);
    bits.push(waitLabel + (secs >= 3 ? ` ${secs}s` : "") + (waitDetail ? ` · ${waitDetail}` : ""));
    if (secs >= 25 && !waitWarned) {
      waitWarned = true;
      toast("OpenStreetMap's servers are slow just now — the panorama is already usable, names will follow.", 7000);
    }
  }
  if (state.horizon) bits.push(`eye ${formatElevation(state.horizon.observer.elevation)}`);
  setStatus(bits.join(" · "));
}

// ---------- compute pipeline ----------
function setLocation(lat: number, lon: number, name = ""): void {
  state.lat = lat;
  state.lon = lon;
  state.name = name || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  state.horizon = null;
  state.peaks = [];
  state.peaksFinal = false;
  state.water = null;
  renderer.setHorizon(null);
  renderer.setPeaks([]);
  renderer.setWater(null);
  renderer.selected = null;
  hidePeakCard();
  $("place-name").textContent = state.name;
  document.title = `${state.name} · Summit`;
  const id = ++requestId;
  showLoading("Downloading terrain…", 0);
  waitWarned = false;
  endWait();
  const msg: WorkerRequest = { type: "compute", id, lat, lon, altitude: state.altitude, water: state.showWater };
  worker.postMessage(msg);
  writeHash();
}

worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
  const m = ev.data;
  if (m.id !== requestId) return;
  switch (m.type) {
    case "progress": {
      const labels = {
        tiles: "Downloading terrain…", horizon: "Computing the skyline…", peaks: "Finding peaks…",
        "peaks-far": "Looking for distant peaks…", water: "Adding lakes and rivers…",
      };
      // The overlay belongs to the two stages that have nothing to show yet. Everything after that
      // reports in the status line, under a panorama the user can already look at and pan around.
      if (m.stage === "tiles" || m.stage === "horizon") {
        showLoading(labels[m.stage], m.fraction, m.detail ?? "");
      } else {
        hideLoading();
        beginWait(labels[m.stage], m.detail);
      }
      break;
    }
    case "horizon":
      state.horizon = m.result;
      renderer.setHorizon(m.result);
      // The skyline is drawn and interactive — showing it beats blocking it behind a progress bar
      // that cannot move until a remote server answers.
      hideLoading();
      beginWait("Finding peaks…");
      break;
    case "peaks": {
      state.peaks = m.peaks;
      state.peaksFinal = m.final;
      renderer.setPeaks(m.peaks);
      hideLoading();
      if (m.final) endWait();
      else updateStatus();
      break;
    }
    case "water":
      state.water = m.result;
      renderer.setWater(m.result);
      updateStatus();
      break;
    case "error":
      if (m.fatal) {
        hideLoading();
        endWait();
        toast(m.message, 6000);
      } else toast(m.message, 5000);
      break;
  }
};

// ---------- pointer interaction ----------
let drag: { x: number; y: number; heading: number; pitch: number; moved: boolean; id: number } | null = null;
const pointers = new Map<number, { x: number; y: number }>();
let pinchStart: { dist: number; fov: number } | null = null;

canvas.addEventListener("pointerdown", (e) => {
  canvas.setPointerCapture(e.pointerId);
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pointers.size === 1) {
    drag = { x: e.clientX, y: e.clientY, heading: state.compass ? state.headingOffset : renderer.view.heading, pitch: renderer.view.pitch, moved: false, id: e.pointerId };
    canvas.classList.add("dragging");
  } else if (pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    pinchStart = { dist: Math.hypot(a.x - b.x, a.y - b.y), fov: renderer.view.fov };
    drag = null;
  }
});
canvas.addEventListener("pointermove", (e) => {
  if (!pointers.has(e.pointerId)) return;
  pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pinchStart && pointers.size === 2) {
    const [a, b] = [...pointers.values()];
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    setFov((pinchStart.fov * pinchStart.dist) / Math.max(20, d));
    return;
  }
  if (!drag || drag.id !== e.pointerId) return;
  const dx = e.clientX - drag.x;
  const dy = e.clientY - drag.y;
  if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
  const deg = dx / renderer.pxPerDeg;
  if (state.compass) state.headingOffset = drag.heading - deg;
  else renderer.view.heading = ((drag.heading - deg) % 360 + 360) % 360;
  if (!state.camera || !state.compass) renderer.view.pitch = Math.max(-45, Math.min(60, drag.pitch + dy / renderer.pxPerDeg));
  renderer.invalidate();
  writeHash();
});
const endPointer = (e: PointerEvent) => {
  pointers.delete(e.pointerId);
  if (pointers.size < 2) pinchStart = null;
  if (drag && drag.id === e.pointerId) {
    if (!drag.moved) selectPeak(renderer.peakAt(e.clientX, e.clientY));
    drag = null;
    canvas.classList.remove("dragging");
  }
};
canvas.addEventListener("pointerup", endPointer);
canvas.addEventListener("pointercancel", endPointer);
canvas.addEventListener("wheel", (e) => {
  e.preventDefault();
  setFov(renderer.view.fov * Math.exp(e.deltaY * 0.0015));
}, { passive: false });
window.addEventListener("keydown", (e) => {
  if ((e.target as HTMLElement).tagName === "INPUT") return;
  const step = renderer.view.fov / 20;
  if (e.key === "ArrowLeft") renderer.view.heading = (renderer.view.heading - step + 360) % 360;
  else if (e.key === "ArrowRight") renderer.view.heading = (renderer.view.heading + step) % 360;
  else if (e.key === "ArrowUp") renderer.view.pitch = Math.min(60, renderer.view.pitch + step / 2);
  else if (e.key === "ArrowDown") renderer.view.pitch = Math.max(-45, renderer.view.pitch - step / 2);
  else if (e.key === "+" || e.key === "=") setFov(renderer.view.fov / 1.2);
  else if (e.key === "-") setFov(renderer.view.fov * 1.2);
  else if (e.key === "Escape") { hideSearch(); hidePeakCard(); }
  else return;
  renderer.invalidate();
  writeHash();
});
function setFov(fov: number): void {
  renderer.view.fov = Math.max(10, Math.min(160, fov));
  renderer.invalidate();
  writeHash();
}

// ---------- peak card ----------
function selectPeak(p: VisiblePeak | null): void {
  renderer.selected = p;
  renderer.invalidate();
  if (!p) return hidePeakCard();
  $("peak-name").textContent = p.name;
  $("peak-ele").textContent = p.elevation ? formatElevation(p.elevation) : `≈ ${formatElevation(p.elevationUsed)}`;
  $("peak-dist").textContent = formatDistance(p.distance);
  $("peak-dir").textContent = `${p.azimuth.toFixed(0)}° ${compassPoint(p.azimuth)}`;
  $("peak-angle").textContent = `${p.angle >= 0 ? "+" : ""}${p.angle.toFixed(1)}°`;
  const links: string[] = [];
  if (p.wikipedia) {
    const [lang, title] = p.wikipedia.includes(":") ? p.wikipedia.split(/:(.+)/) : ["en", p.wikipedia];
    links.push(`<a href="https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, "_"))}" target="_blank" rel="noopener">Wikipedia</a>`);
  } else if (p.wikidata) links.push(`<a href="https://www.wikidata.org/wiki/${p.wikidata}" target="_blank" rel="noopener">Wikidata</a>`);
  links.push(`<a href="https://www.openstreetmap.org/node/${p.id.slice(1)}" target="_blank" rel="noopener">OpenStreetMap</a>`);
  links.push(`<a href="#" data-goto>View from here</a>`);
  $("peak-links").innerHTML = links.join("");
  $("peak-links").querySelector("[data-goto]")?.addEventListener("click", (e) => {
    e.preventDefault();
    state.altitude = null;
    setLocation(p.lat, p.lon, p.name);
  });
  $("peak-card").hidden = false;
}
function hidePeakCard(): void {
  $("peak-card").hidden = true;
}
$("peak-close").onclick = () => selectPeak(null);

// ---------- search ----------
const searchEl = $("search");
const searchInput = $<HTMLInputElement>("search-input");
const resultsEl = $("search-results");
function showSearch(): void {
  searchEl.hidden = false;
  searchInput.focus();
  searchInput.select();
}
function hideSearch(): void {
  searchEl.hidden = true;
}
$("btn-search").onclick = showSearch;
$("search-close").onclick = hideSearch;
$("search-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const q = searchInput.value.trim();
  if (!q) return;
  const coords = q.match(/^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (coords) {
    state.altitude = null;
    setLocation(parseFloat(coords[1]), parseFloat(coords[2]));
    hideSearch();
    return;
  }
  resultsEl.innerHTML = `<li class="muted">Searching…</li>`;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=7&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const items = (await res.json()) as { display_name: string; lat: string; lon: string; name?: string; type?: string }[];
    resultsEl.innerHTML = "";
    if (!items.length) resultsEl.innerHTML = `<li class="muted">Nothing found.</li>`;
    for (const it of items) {
      const li = document.createElement("li");
      li.tabIndex = 0;
      const name = it.name || it.display_name.split(",")[0];
      li.innerHTML = `<strong></strong><small></small>`;
      li.querySelector("strong")!.textContent = name;
      li.querySelector("small")!.textContent = `${it.type ?? ""} · ${it.display_name}`;
      const go = () => {
        state.altitude = null;
        setLocation(parseFloat(it.lat), parseFloat(it.lon), name);
        hideSearch();
      };
      li.onclick = go;
      li.onkeydown = (ke) => ke.key === "Enter" && go();
      resultsEl.appendChild(li);
    }
  } catch {
    resultsEl.innerHTML = `<li class="muted">Search failed. Check your connection.</li>`;
  }
});

// ---------- geolocation ----------
$("btn-locate").onclick = async () => {
  const btn = $("btn-locate");
  btn.classList.add("active");
  try {
    const pos = await getPosition();
    state.altitude = pos.altitude;
    setLocation(pos.lat, pos.lon, "My position");
    if (pos.accuracy > 100) toast(`Position accuracy is only ±${Math.round(pos.accuracy)} m.`);
  } catch (e) {
    toast((e as Error).message);
  } finally {
    btn.classList.remove("active");
  }
};

// ---------- water ----------
function setWaterLayer(on: boolean): void {
  state.showWater = on;
  renderer.showWater = on;
  localStorage.setItem("summit.water", on ? "1" : "0");
  $("btn-water").classList.toggle("active", on);
  renderer.invalidate();
  updateStatus();
  // Turned on after the viewpoint was computed without it: fetch the layer now.
  if (on && !state.water && Number.isFinite(state.lat)) setLocation(state.lat, state.lon, state.name);
}
$("btn-water").onclick = () => setWaterLayer(!state.showWater);
setWaterLayer(state.showWater);

// ---------- compass ----------
async function setCompass(on: boolean): Promise<void> {
  const btn = $("btn-compass");
  if (!on) {
    stopOrientation?.();
    stopOrientation = null;
    state.compass = false;
    btn.classList.remove("active");
    return;
  }
  try {
    let warned = false;
    stopOrientation = await watchOrientation((o: Orientation) => {
      renderer.view.heading = (o.heading + state.headingOffset + 360) % 360;
      if (state.camera && o.upright) renderer.view.pitch = Math.max(-45, Math.min(60, o.pitch));
      if (!o.absolute && !warned) {
        warned = true;
        toast("Compass heading is relative on this device. Drag to align it with a known peak.", 6000);
      }
      renderer.invalidate();
    });
    state.compass = true;
    state.headingOffset = 0;
    btn.classList.add("active");
  } catch (e) {
    toast((e as Error).message);
  }
}
$("btn-compass").onclick = () => setCompass(!state.compass);

// ---------- camera (AR) ----------
async function setCamera(on: boolean): Promise<void> {
  const btn = $("btn-camera");
  if (!on) {
    stopCamera(video);
    video.hidden = true;
    state.camera = false;
    renderer.transparent = false;
    btn.classList.remove("active");
    renderer.view.fov = parseFloat(localStorage.getItem("summit.fov") || "") || 80;
    renderer.invalidate();
    return;
  }
  try {
    await startCamera(video);
    video.hidden = false;
    state.camera = true;
    renderer.transparent = true;
    btn.classList.add("active");
    localStorage.setItem("summit.fov", String(renderer.view.fov));
    // Typical phone rear camera: ~50° horizontal in portrait. Pinch to calibrate; remembered per device.
    renderer.view.fov = parseFloat(localStorage.getItem("summit.camFov") || "") || (window.innerWidth < window.innerHeight ? 50 : 66);
    renderer.invalidate();
    if (!state.compass) await setCompass(true);
    toast("Pinch to match the overlay to the camera's zoom. Drag sideways to fine-tune the heading.", 6000);
  } catch (e) {
    toast((e as Error).message);
  }
}
$("btn-camera").onclick = () => setCamera(!state.camera);
window.addEventListener("beforeunload", () => {
  if (state.camera) localStorage.setItem("summit.camFov", String(renderer.view.fov));
});

// ---------- share ----------
$("btn-share").onclick = async () => {
  writeHash();
  await new Promise((r) => setTimeout(r, 300));
  const url = location.href;
  const title = `${state.name} · Summit panorama`;
  try {
    if (navigator.share) await navigator.share({ title, url });
    else {
      await navigator.clipboard.writeText(url);
      toast("Link copied to the clipboard.");
    }
  } catch {
    /* user cancelled */
  }
};

// ---------- boot ----------
const h = readHash();
if (h.hdg !== undefined) renderer.view.heading = h.hdg;
if (h.fov !== undefined) renderer.view.fov = Math.max(10, Math.min(160, h.fov));
if (h.pitch !== undefined) renderer.view.pitch = h.pitch;
if (h.lat !== undefined && h.lon !== undefined) setLocation(h.lat, h.lon, h.name ?? "");
else {
  // Default viewpoint until the user picks one: Rigi Kulm, a classic 360° panorama.
  renderer.view.heading = 180;
  setLocation(47.0567, 8.4853, "Rigi Kulm");
  toast("Tap ⌖ to use your position, or search a place.", 5000);
}
window.addEventListener("hashchange", () => {
  const n = readHash();
  if (n.lat !== undefined && n.lon !== undefined && (n.lat !== state.lat || n.lon !== state.lon)) setLocation(n.lat, n.lon, n.name ?? "");
});
